import { Box, Button, Flex, IconFont, Input, Select } from '@byecode/ui'
import type { DataSourceAbstract } from '@lighthouse/core'
import { FieldTypeTag, getFieldOptions } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { Fragment, useMemo } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import type { PageVariableFormSlice } from '../../../formSlice'
import * as SC from './styles'

interface Props {
    dataSource: DataSourceAbstract
}

type VariableItemError = {
    name?: {
        message?: string
    }
    fieldId?: {
        message?: string
    }
}

export const VariableList = ({ dataSource }: Props) => {
    const { control, formState } = useFormContext<PageVariableFormSlice>()

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'items',
        rules: { required: '请至少添加一条输出变量', minLength: { value: 1, message: '请至少添加一条输出变量' } }
    })
    const { schema, viewOptions: { tableProps = [] } = {} } = dataSource || {}
    const fieldOptions = useMemo(() => {
        return getFieldOptions(schema, tableProps).map(item => ({
            ...item,
            extra: <FieldTypeTag type={item.type} innerType={item.innerType} />
        }))
    }, [schema, tableProps])

    return (
        <Box>
            <SC.FormItemHeader>
                <SC.FormLabel required>输出变量</SC.FormLabel>
            </SC.FormItemHeader>
            <Flex py={8} direction="column" gap={8}>
                {fields.map((item, index) => (
                    <Fragment key={item.id}>
                        {(() => {
                            const itemError = formState.errors.items?.[index] as VariableItemError | undefined
                            const errorMessage = itemError?.name?.message || itemError?.fieldId?.message
                            return (
                                <>
                                    <Flex gap={8} alignItems="center">
                                        <Controller
                                            control={control}
                                            name={`items.${index}.name`}
                                            render={({ field }) => (
                                                <Input {...field} placeholder="变量名称" size="lg" style={{ flex: 1 }} />
                                            )}
                                            rules={{ required: '请输入变量名称' }}
                                        />
                                        <Controller
                                            control={control}
                                            name={`items.${index}.fieldId`}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    placeholder="选择变量值"
                                                    searchable
                                                    options={fieldOptions}
                                                    size="lg"
                                                    styles={{ root: { flex: 1 } }}
                                                />
                                            )}
                                            rules={{ required: '请选择变量字段' }}
                                        />
                                        <SC.DeleteButton onClick={() => remove(index)}>
                                            <IconFont type="Close" />
                                        </SC.DeleteButton>
                                    </Flex>
                                    {errorMessage && <SC.ErrorMessage>{errorMessage}</SC.ErrorMessage>}
                                </>
                            )
                        })()}
                    </Fragment>
                ))}
                <Button
                    style={{ alignSelf: 'flex-start' }}
                    radius={100}
                    icon={<IconFont type="Add" fill="var(--color-gray-500)" />}
                    onClick={() => {
                        append(
                            {
                                id: nanoid(),
                                name: '',
                                fieldId: ''
                            },
                            { shouldFocus: false }
                        )
                    }}
                >
                    添加
                </Button>

                {formState.errors.items?.root?.message && <SC.ErrorMessage>{formState.errors.items?.root?.message}</SC.ErrorMessage>}
            </Flex>
        </Box>
    )
}
